import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { CollectionTypes } from "../types/CollectionTypes";
import { ICollection, ITheme } from "../types/ICollection";
import { IMap } from "../types/IMap";
import { IWorkspace } from "../types/IWorkspace";

interface PagesState {
  data: IDataState;
  truterritory: ITruTerritoryState;
}

interface IDataState {
  showArchived: boolean;
  selectedTypesFilter: string[];
  searchString?: string;
  consoleCollection?: ICollection;
  currentlyEditingCollection?: ICollection["ID"];
}

export interface ITruTerritoryState {
  map?: IMap & { collections: ICollection[] };
  workspace?: IWorkspace & { collections: ICollection[] };
  workspaces?: IWorkspace[];
  currentThemes: Record<string, ITheme>;

  currentEditingCollection?: {
    properties: ICollection["properties"];
    aggregates: ICollection["aggregates"];
  };
  currentEditingTheme?: ITheme;

  topPanelSections: {
    layers: boolean;
    theme: boolean;
    mapReports: boolean;
  };
  isSidePanelOpen: boolean;
  isMapDirty: boolean;
  isSnippetToolOpen: boolean;
}

export const initialState: PagesState = {
  data: { showArchived: false, selectedTypesFilter: CollectionTypes.map(({ type }) => type) },
  truterritory: {
    currentThemes: {},
    topPanelSections: {
      layers: false,
      theme: false,
      mapReports: false,
    },
    isSidePanelOpen: false,
    isMapDirty: false,
    isSnippetToolOpen: false,
  },
};

export const pagesSlice = createSlice({
  name: "pages",
  initialState,
  reducers: {
    // data collections
    toggleShowArchived: (state) => {
      state.data.showArchived = !state.data.showArchived;
    },
    toggleType: (state, { payload }: PayloadAction<IDataState["selectedTypesFilter"][0]>) => {
      if (state.data.selectedTypesFilter.includes(payload)) {
        state.data.selectedTypesFilter = state.data.selectedTypesFilter.filter((t) => t !== payload);
      } else {
        state.data.selectedTypesFilter.push(payload);
      }
    },
    setDataSearch: (state, { payload }: PayloadAction<IDataState["searchString"]>) => {
      state.data.searchString = payload;
    },
    resetDataState: (state) => {
      state.data = initialState.data;
    },

    setConsoleCollection: (state, { payload }: PayloadAction<IDataState["consoleCollection"]>) => {
      state.data.consoleCollection = payload;
    },
    setCurrentlyEditingCollection: (state, { payload }: PayloadAction<IDataState["currentlyEditingCollection"]>) => {
      state.data.currentlyEditingCollection = payload;
    },

    // truterritory
    resetTruTerritoryState: (state) => {
      state.truterritory = initialState.truterritory;
    },
    setMap: (state, { payload: map }: PayloadAction<ITruTerritoryState["map"]>) => {
      state.truterritory.map = map;
      state.truterritory.currentThemes = {};

      if (!map) return;

      for (let i = 0; i < map.collections.length; i++) {
        const collection = map.collections[i];
        const theme = collection.findTheme ? collection.findTheme(map, false) : null;
        if (!theme) continue;

        state.truterritory.currentThemes[collection.ID] = theme;
      }
    },
    setCollectionOrder: (state, { payload }: PayloadAction<ICollection["ID"][]>) => {
      if (!state.truterritory.map) return;

      const newCollections = payload.map((id) => state.truterritory.map!.collections.find((c) => c.ID === id)!);
      state.truterritory.map.collections = newCollections;
    },
    toggleTopPanelSection: (state, { payload }: PayloadAction<keyof ITruTerritoryState["topPanelSections"]>) => {
      state.truterritory.topPanelSections[payload] = !state.truterritory.topPanelSections[payload];

      // close all the other panels
      Object.keys(state.truterritory.topPanelSections).forEach((section) => {
        if (section !== payload) state.truterritory.topPanelSections[section as keyof ITruTerritoryState["topPanelSections"]] = false;
      });
    },
    setSidePanelOpen(state, { payload }: PayloadAction<boolean>) {
      state.truterritory.isSidePanelOpen = payload;
    },
    setMapDirty(state, { payload }: PayloadAction<boolean>) {
      state.truterritory.isMapDirty = payload;
    },
    setIsSnippetToolOpen(state, { payload }: PayloadAction<boolean>) {
      state.truterritory.isSnippetToolOpen = payload;
    },
    setCurrentlyEditingThemeAndCollection: (
      state,
      { payload: { theme, collection } }: PayloadAction<{ theme: ITheme; collection: Required<ITruTerritoryState>["currentEditingCollection"] }>
    ) => {
      state.truterritory.currentEditingTheme = theme;
      state.truterritory.currentEditingCollection = {
        properties: collection.properties,
        aggregates: collection.aggregates,
      };
    },
    updateCurrentlyEditingTheme: (state, { payload }: PayloadAction<Partial<ITheme>>) => {
      if (!state.truterritory.currentEditingTheme) return;
      state.truterritory.currentEditingTheme = { ...state.truterritory.currentEditingTheme, ...payload };
    },
    setWorkspace: (state, { payload: workspace }: PayloadAction<ITruTerritoryState["workspace"]>) => {
      state.truterritory.workspace = workspace;
    },
    setWorkspaces(state, { payload: workspaces }: PayloadAction<ITruTerritoryState["workspaces"]>) {
      state.truterritory.workspaces = workspaces;
    },
  },
});

export const {
  toggleShowArchived,
  toggleType,
  setDataSearch,
  resetDataState,
  setConsoleCollection,
  setCurrentlyEditingCollection,
  resetTruTerritoryState,
  setMap,
  setCollectionOrder,
  toggleTopPanelSection,
  setSidePanelOpen,
  setCurrentlyEditingThemeAndCollection,
  updateCurrentlyEditingTheme,
  setWorkspace,
  setWorkspaces,
  setMapDirty,
  setIsSnippetToolOpen,
} = pagesSlice.actions;

export default pagesSlice.reducer;
